// Lightweight, zero-dependency confetti effect inspired by wukko.me
// Renders to a fixed full-screen canvas with 60fps physics

interface Particle {
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  vx: number;
  vy: number;
  rotation: number;
  vRot: number;
  opacity: number;
}

const COLORS = [
  '#f87171', '#fb923c', '#fbbf24', '#34d399',
  '#60a5fa', '#818cf8', '#a78bfa', '#f472b6',
  '#facc15', '#38bdf8'
];

let activeCanvas: HTMLCanvasElement | null = null;
let animationFrameId: number | null = null;
let particles: Particle[] = [];

export function launchConfetti(originX?: number, originY?: number): void {
  if (typeof window === 'undefined') return;

  // Check prefers-reduced-motion
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  // Create canvas if not already present
  if (!activeCanvas) {
    activeCanvas = document.createElement('canvas');
    activeCanvas.setAttribute(
      'style',
      'position: fixed; inset: 0; width: 100vw; height: 100vh; pointer-events: none; z-index: 9999;'
    );
    document.body.appendChild(activeCanvas);

    const resize = () => {
      if (!activeCanvas) return;
      const dpr = window.devicePixelRatio || 1;
      activeCanvas.width = window.innerWidth * dpr;
      activeCanvas.height = window.innerHeight * dpr;
    };
    resize();
    window.addEventListener('resize', resize, { passive: true });
  }

  const dpr = window.devicePixelRatio || 1;
  const startX = (originX !== undefined ? originX : window.innerWidth / 2) * dpr;
  const startY = (originY !== undefined ? originY : window.innerHeight * 0.35) * dpr;

  const count = 45;
  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
    const speed = (Math.random() * 8 + 4) * dpr;
    particles.push({
      x: startX,
      y: startY,
      w: (Math.random() * 6 + 6) * dpr,
      h: (Math.random() * 4 + 4) * dpr,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      vx: Math.cos(angle) * speed * (0.8 + Math.random() * 0.6),
      vy: Math.sin(angle) * speed - (Math.random() * 5 + 3) * dpr,
      rotation: Math.random() * Math.PI * 2,
      vRot: (Math.random() - 0.5) * 0.25,
      opacity: 1,
    });
  }

  if (!animationFrameId) {
    const ctx = activeCanvas.getContext('2d');
    if (!ctx) return;

    const animate = () => {
      if (!activeCanvas || !ctx) return;
      ctx.clearRect(0, 0, activeCanvas.width, activeCanvas.height);

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.22 * dpr; // gravity
        p.vx *= 0.985;      // friction
        p.rotation += p.vRot;
        p.opacity -= 0.009; // fade out

        if (p.opacity <= 0 || p.y > activeCanvas.height + 20) {
          particles.splice(i, 1);
          continue;
        }

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.globalAlpha = Math.max(0, p.opacity);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }

      if (particles.length > 0) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        if (activeCanvas && activeCanvas.parentNode) {
          activeCanvas.parentNode.removeChild(activeCanvas);
          activeCanvas = null;
        }
        animationFrameId = null;
      }
    };

    animationFrameId = requestAnimationFrame(animate);
  }
}
